import React, { useState } from 'react'
import styles from './ViewEvents.module.css';
import propertyEventStyles from '../KeyDropPickup.module.css'
import OutlinedCloseIcon from '../../svgIcons/OtlinedCloseIcon';
import parsePhoneNumberFromString from 'libphonenumber-js';
import moment from 'moment';
import InfoIcon from '../../svgIcons/InfoIcon';
import DeleteIcon from '../../svgIcons/DeleteIcon';
import { AppDispatch } from '../../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import CustModal from '../../common/custmodal/CustModal';
import { deletePropertyEvents } from '../../../redux/key-pickup-drop-events/actions/key-pickup-drop-events.action';
import Loader from '../../common/loader/Loader';

interface ViewPropertyEventsProps {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<React.SetStateAction<string>>;
    setToastType: React.Dispatch<React.SetStateAction<string>>;
    propertyEventList: {
        locationId: string;
        name: string;
        email: string;
        startDate: string;
        endDate: string;
        startTime: string;
        endTime: string;
        locationName: string;
        phone: string;
        locker: string;
        id: string;
        description?: string;
        eventName: string;
        accessCode: string;
    };
    onDeleteSuccess?: () => void; // Callback to refetch data after successful delete
}

const ViewEvents: React.FC<ViewPropertyEventsProps> = ({
    setOpen,
    setShowToast,
    setToastMessage,
    setToastType,
    propertyEventList,
    onDeleteSuccess
}) => {

    const dispatch: AppDispatch = useDispatch();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
    };

    const handleConfirmDelete = () => {
        if (propertyEventList?.id) {
            setIsDeleting(true);
            setShowConfirmModal(false);

            dispatch(
                deletePropertyEvents(
                    propertyEventList.id,
                    (message: string) => {
                        // Success callback
                        setIsDeleting(false);
                        setToastType('success');
                        setShowToast(true);
                        setToastMessage(message);
                        setOpen(false); // Close the modal
                        onDeleteSuccess?.(); // Refetch data
                    },
                    (message: string) => {
                        // Error callback
                        setIsDeleting(false);
                        setToastType('error');
                        setShowToast(true);
                        setToastMessage(message);
                    },
                ),
            );
        }
    };

    function formatPhone(phone?: string): string {
        if (!phone) return '';
        const parsed = parsePhoneNumberFromString(phone);
        return parsed ? parsed.formatInternational() : phone;
    }

    const getEventBadgeVariant = (eventName: string) => {
        const lowerEventName = eventName?.toLowerCase() || '';

        // Check for both pickup and drop
        if ((lowerEventName.includes('pick') || lowerEventName.includes('up')) &&
            lowerEventName.includes('drop')) {
            return 'yellow';
        }
        // Check for pickup/up
        else if (lowerEventName.includes('pick') || lowerEventName.includes('up')) {
            return 'green';
        }
        // Check for drop
        else if (lowerEventName.includes('drop')) {
            return 'red';
        }

        return 'default';
    };

    const ConfirmationModal = () => (
        <div className={propertyEventStyles.keyDropFeaturesActionModal}>
            <h2 className={propertyEventStyles.keyDropFeaturesTitle}>Are you sure you want to delete?</h2>
            <p className={propertyEventStyles.keyDropFeaturesDescription}>Vendor appointment cannot be recovered once deleted.</p>
            <div className={propertyEventStyles.keyDropFeaturesActions}>
                <button
                    className={propertyEventStyles.keyDropFeaturesCancelButton}
                    onClick={handleCancelDelete}
                    disabled={isDeleting}>
                    Cancel
                </button>
                <button
                    className={propertyEventStyles.keyDropFeaturesConfirmButton}
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
            </div>
        </div>
    );

    const handleDeleteClick = () => {
        setShowConfirmModal(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    return (
        <div className={styles.modalContainer}>
            {isDeleting && <Loader />}
            <div className={styles.modalHeader}>
                <div className={styles.headerContent}>
                    <div className={styles.vendorInfo}>
                        <div className={styles.vendorDetails}>
                            <h2 className={styles.vendorName}>{propertyEventList.name}</h2>
                            <p className={styles.appointmentId}>Property: {propertyEventList?.locationName || '-'}</p>
                        </div>
                    </div>
                    <div className={styles.eventBadgeWrapper}>
                        <span className={`${styles.eventBadge} ${styles[`eventBadge${getEventBadgeVariant(propertyEventList.eventName).charAt(0).toUpperCase() + getEventBadgeVariant(propertyEventList.eventName).slice(1)}`]}`}>
                            {propertyEventList.eventName}
                        </span>
                    </div>
                </div>
                <button className={styles.closeButton} onClick={handleClose} aria-label="Close modal">
                    <OutlinedCloseIcon fill="#fff" />
                </button>
            </div>

            {/* Modal Body */}
            <div className={styles.modalBody}>
                <div className={styles.twoColumnLayout}>
                    {/* Start Date */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Start Date</label>
                            <span className={styles.infoValue}>
                                {moment(propertyEventList?.startDate).format('YYYY-MM-DD')}
                            </span>
                        </div>
                    </div>

                    {/* Start Time */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Start Time</label>
                            <span className={styles.infoValue}>
                                {propertyEventList?.startTime}
                            </span>
                        </div>
                    </div>

                    {/* End Date */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>End Date</label>
                            <span className={styles.infoValue}>
                                {moment(propertyEventList?.endDate).format('YYYY-MM-DD')}
                            </span>
                        </div>
                    </div>

                    {/* End Time */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>End Time</label>
                            <span className={styles.infoValue}>
                                {propertyEventList?.endTime}
                            </span>
                        </div>
                    </div>

                    {/* Locker Assignment */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Locker</label>
                            <span className={styles.infoValue}>{propertyEventList.locker}</span>
                        </div>
                    </div>

                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Access Code</label>
                            <span className={styles.infoValue}>{propertyEventList.accessCode}</span>
                        </div>
                    </div>

                    {/* Email */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Email</label>
                            <span className={styles.infoValue}>{propertyEventList.email || "N/A"}</span>
                        </div>
                    </div>

                    {/* Phone */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Phone</label>
                            <span className={styles.infoValue}>{propertyEventList.phone ? formatPhone(`+${propertyEventList.phone}`) : "N/A"}</span>
                        </div>
                    </div>

                    {/* Notes */}
                    {propertyEventList?.description && (
                        <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                            <div className={styles.infoContent}>
                                <label className={styles.infoLabel}>Notes</label>
                                <span className={styles.infoValue}>{propertyEventList?.description}</span>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Modal Footer */}
            <div className={styles.modalFooter}>
                <button className={styles.deleteModalButton} onClick={handleDeleteClick} disabled={isDeleting}>
                    <DeleteIcon size={18} fill="#dc3545" />
                    <span>Delete Event</span>
                </button>
                <button className={styles.closeModalButton} onClick={handleClose} disabled={isDeleting}>
                    Close
                </button>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <CustModal
                    open={showConfirmModal}
                    setOpen={setShowConfirmModal}
                    bodyData={<ConfirmationModal />}
                    onCloseModal={handleCancelDelete}
                />
            )}
        </div>
    )
}

export default ViewEvents
